import { useCallback, useEffect, useState } from "react";

import { CallCenterUserState } from "@/Common/Api/CallCenter/Responses/CallCenterUserState";
import { ContactMatchingPhoneNumberResult } from "@/Common/Api/CallCenter/Responses/ContactMatchingPhoneNumberResult";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { CallCenterHelper } from "@/Common/Helpers/CallCenterHelper";
import { CallCenterCurrentState } from "@/Common/Models/CallCenter/CallCenterCallState";
import { CallCenterCallType } from "@/Common/Models/CallCenter/CallCenterCallType";
import { CallIdAndNumber } from "@/Common/Models/CallCenter/CallIdAndNumber";
import { CallMatches } from "@/Common/Models/CallCenter/CallMatches";
import { Guid } from "@/Common/Models/Guid";
import { PatientTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { ButtonControl } from "../Buttons/ButtonControl";
import { LoadingIndicator } from "../Loader/LoadingIndicator";
import { CallCenterCallStatus } from "./CallCenterCallStatus";
import { MatchedIndividualList } from "./MatchedIndividualList";

import styles from "./ViewPatientMatches.module.scss";

import ExpandSvg from "@/Common/_images/Expand.svg";
import MinimizeSvg from "@/Common/_images/Minimize.svg";
import loadingGif from "@/Globals/_images/loading.gif";

interface IViewPatientMatchesProps {
    userState: CallCenterUserState;
    callMatches: CallMatches;
    isMatchError: boolean;
    dragClass: string;
    profilePictureVersion: Guid;
    onClose: () => void;
    onRetry: () => void;
    onSizeChanged: () => void;
    onContactSelected: (contactMatching: ContactMatchingPhoneNumberResult) => Promise<boolean>;
}

const EmptyCall = new CallIdAndNumber(null, null);

function ViewPatientMatches(props: Readonly<IViewPatientMatchesProps>): JSX.Element {
    const [minimized, setMinimized] = useState(true); // start minimized so we can stay minimized with auto-select
    const [autoSelectedCallId, setAutoSelectedCallId] = useState<CallIdAndNumber>(EmptyCall);
    const isInternalNumber = !IsNullOrWhiteSpace(props.userState.CallerNumber) && !CallCenterHelper.is10DigitPhoneNumber(props.userState.CallerNumber);

    const setWindowMinimized = useCallback((minimized: boolean) => {
        setMinimized(minimized);
        props.onSizeChanged();
    }, [props]);

    function onMinimizeClick(): void {
        setWindowMinimized(!minimized);
    }

    function getQueueBackgroundColor(state: CallCenterCurrentState): string {
        switch (state) {
            case CallCenterCurrentState.OnHold:
                return "#EE8A2C";
            case CallCenterCurrentState.InCall:
            default:
                return "#329047";
        }
    }

    function getQueueBanner(state: CallCenterUserState): JSX.Element {
        const queueName = IsNullOrWhiteSpace(state.CallQueue) ? null : state.CallQueue;
        const incomingLabel = queueName == null
            ? cacheStore.FindLookupByLookupCode("IncomingCall", "RecallStatusType").GetValue()
            : cacheStore.GetString("CallCenter_IncomingQueueLabel");
        const queueBackgroundColor = getQueueBackgroundColor(state.CurrentState);

        return (
            <div className={styles.CallQueue} style={{ backgroundColor: queueBackgroundColor }}>
                <div>{incomingLabel}</div>
                <div>{queueName}</div>
            </div>
        );
    }

    function getCallerIDBanner(state: CallCenterUserState): JSX.Element {
        let formattedNumber = null;
        if (IsNullOrWhiteSpace(state.CallerNumber)) {
            formattedNumber = cacheStore.GetString("CallCenter_UnknownCallerNumber");
        }
        else if (CallCenterHelper.is10DigitPhoneNumber(state.CallerNumber)) {
            formattedNumber = PhoneNumber.stringFormattedPhone(new PhoneNumber({ Number: state.CallerNumber }), false);
        }
        else {
            formattedNumber = PhoneNumber.stringFormattedPhone(new PhoneNumber({ Extension: state.CallerNumber }), true);
        }
        const callerName = state.CallerName ?? cacheStore.GetString("CallCenter_UnknownCallerName");

        return (
            <div className={styles.CallerID}>
                <div>{callerName}</div>
                <div>{formattedNumber}</div>
            </div>
        );
    }

    function getButtonBar(): JSX.Element {
        return (
            <div className={styles.BottomBar}>
                <ButtonControl variant="neutral" label={cacheStore.GetString("CloseLabel")} onClick={props.onClose} />
            </div>
        );
    }

    function getMatchedList(matches: CallMatches): JSX.Element {
        const matchesCollection = matches?.Matches;
        if (matchesCollection == null) {
            return (
                <div className={styles.PatientList}>
                    <div className={styles.Loading}>
                        <LoadingIndicator imagePath={loadingGif} />
                    </div>
                </div>
            );
        }

        if (matchesCollection.count() == 0) {
            const noResultsLabel = cacheStore.GetString("CallCenter_NoPatientMatches");

            return (
                <div className={styles.PatientList}>
                    <div className={styles.NoResults}>
                        {noResultsLabel}
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.PatientList}>
                <MatchedIndividualList
                    matches={matchesCollection.toArray()}
                    onItemClick={OnContactSelected}
                    profilePictureVersion={props.profilePictureVersion}
                />
            </div>
        );
    }

    function getProfessionalCallLabel(): JSX.Element {
        const professionalCallLabel = cacheStore.GetString("CallCenter_ProfessionalCall");
        return (
            <div className={styles.PatientList}>
                <div className={styles.ProfessionalCall}>
                    {professionalCallLabel}
                </div>
            </div>
        );
    }

    function getListError(): JSX.Element {
        const failedText = cacheStore.GetString("CallCenter_FailedToLoad");
        const retryText = cacheStore.GetString("RetryLabel");

        return (
            <div className={styles.PatientList}>
                <div className={styles.Failed}>
                    <div className={styles.ErrorMessage}>{failedText}</div>
                    <a onClick={props.onRetry}>{retryText}</a>
                </div>
            </div>
        );
    }

    function getInternalCallLabel(): JSX.Element {
        const internalCallLabel = cacheStore.GetString("CallCenter_InternalCall");

        return (
            <div className={styles.PatientList}>
                <div className={styles.InternalCall}>
                    {internalCallLabel}
                </div>
            </div>
        );
    }

    function getResultsSection(state: CallCenterUserState, matches: CallMatches, isMatchError: boolean): JSX.Element {
        const isProfessionalCall = state.CallType === CallCenterCallType.ProfessionalCall;

        if (isMatchError) {
            return getListError();
        }

        if (isProfessionalCall) {
            return getProfessionalCallLabel();
        }

        if (isInternalNumber) {
            return getInternalCallLabel();
        }

        return getMatchedList(matches);
    }

    function getWindowContent(state: CallCenterUserState, matches: CallMatches, isMatchError: boolean, minimized: boolean): JSX.Element {
        if (minimized) {
            return null;
        }

        const callerIDBanner = getCallerIDBanner(state);
        const queueBanner = getQueueBanner(state);
        const buttonBar = getButtonBar();
        const resultsSection = getResultsSection(state, matches, isMatchError);

        return (
            <>
                {callerIDBanner}
                {queueBanner}
                {resultsSection}
                {buttonBar}
            </>
        );
    }

    // a promise to await on whether patient store is loading or not.
    const waitForPatientStoreLoading = useCallback(() => new Promise<void>((resolve, reject) => {
        let interval: NodeJS.Timeout;
        let rejectionTimeOut: NodeJS.Timeout;

        // preemptively resolve if patient store is not loading
        if (!patientStore.IsLoading) {
            resolve();
            return;
        }

        // we are loading so we wait for it to finish loading
        function waitForChange(): void {
            if (!patientStore.IsLoading) {
                clearInterval(interval);
                clearTimeout(rejectionTimeOut);
                resolve();
            }
        }

        interval = setInterval(waitForChange, 50);
        // regardless of whether or not we finish loading, clear within 15 seconds to prevent stalling
        rejectionTimeOut = setTimeout(() => {
            clearInterval(interval);
            reject(new Error("timed out"));
        }, 15000);
    }), []);

    const navigateToPatient = useCallback(async (contactMatching: ContactMatchingPhoneNumberResult) => {
        if (contactMatching.Individual.Contact_id) {
            try {
                // wait for patient store to finish loading in case it is loading
                await waitForPatientStoreLoading();

                // always load file so we can switch top level tab as well
                sessionStore.dispatch(LocalOperations.Person.LoadFile, {
                    contactId: contactMatching.Individual.Contact_id,
                    contactFacetType: contactMatching.Individual.ContactFacetType
                });

                // wait for this if we don't have patient UI Model yet
                if (patientStore.PatientUIModels[contactMatching.Individual.Contact_id] == null) {
                    await waitForPatientStoreLoading();
                }

                const patientUI = patientStore.PatientUIModels[contactMatching.Individual.Contact_id];

                // if not in patient appointment, move to patient appointment
                if (patientUI != null &&
                    patientUI.get("ActiveVerticalTab") != PatientTab.Appointment) {
                    sessionStore.dispatch(LocalPatientOperations.UI.ChangeVerticalTabs, {
                        value: PatientTab.Appointment
                    });
                }
            }
            catch {
                return;
            }
        }
    }, [waitForPatientStoreLoading]);

    const OnContactSelected = useCallback(async (contactMatching: ContactMatchingPhoneNumberResult): Promise<void> => {
        const success = await props.onContactSelected(contactMatching);
        if (!success) {
            // open window if its closed
            setWindowMinimized(false);
            return;
        }

        navigateToPatient(contactMatching);

        // minimize the window
        setWindowMinimized(true);
    }, [props, setWindowMinimized, navigateToPatient]);

    // auto select single match if only one result loaded
    useEffect(() => {
        if (autoSelectedCallId.Equals(props.callMatches.CallIdAndNumber)) {
            return; // already processed for this call
        }
        // make sure we have what we need before trying to auto-select
        if (props.isMatchError
            || props.callMatches?.Matches == null
            || props.userState.CallStart == null
            || props.callMatches.CallIdAndNumber.CallId == null
            || props.userState.CurrentState != CallCenterCurrentState.InCall) {
            // not yet ready to check for auto-select, show UI incase of error or loading
            setWindowMinimized(false);
            return;
        }
        // mark we've processed this call
        setAutoSelectedCallId(props.callMatches.CallIdAndNumber);
        if (!sessionStore.CallCenter_AutoOpenPatientOnSingleMatch || props.callMatches.Matches.count() != 1) {
            // don't auto select and open UI
            setWindowMinimized(false);
            return;
        }
        // auto select single match
        OnContactSelected(props.callMatches.Matches.first());
    }, [autoSelectedCallId, props, OnContactSelected, setWindowMinimized]);

    const { userState, callMatches, isMatchError } = props;
    const { CurrentState: currentState, CallStart: callStart, CallEnd: callEnd } = userState;
    const windowContent = getWindowContent(userState, callMatches, isMatchError, minimized);
    const minMaxIcon = minimized ? <ExpandSvg /> : <MinimizeSvg />;

    return (
        <>
            <div className={`${props.dragClass} ${styles.Header}`}>
                <div className={styles.MinMaxButton} onClick={onMinimizeClick}>
                    {minMaxIcon}
                </div>
                <CallCenterCallStatus callState={currentState} callStart={callStart} callEnd={callEnd} showDuration={minimized} />
            </div>
            {windowContent}
        </>
    );
}

export { ViewPatientMatches };
